import React, { useEffect, useRef } from 'react';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import 'lite-youtube-embed/src/lite-yt-embed.css';
import 'lite-youtube-embed/src/lite-yt-embed.js';
import type {} from './lite-youtube-jsx';
import { styles } from './YouTubePlayer.css';
import { PlayerState, YouTubeMessageEvent, type LiteYouTubeElement, type YouTubePlayerProps } from './types';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';

export { PlayerState } from './types';
export type {
  LiteYouTubeElement,
  Orientation,
  PlayerStateChangeEvent,
  YouTubeIframePlayer,
  YouTubePlayerProps,
} from './types';

export const ASPECT_RATIOS = {
  landscape: 16 / 9,
  portrait: 9 / 16,
};

const YOUTUBE_ORIGINS = ['https://www.youtube.com', 'https://www.youtube-nocookie.com'];
const YOUTUBE_PLAYER_PLAYING_EVENT = 'jbhi-youtube-player:playing';
type YouTubePlayingEventDetail = { source: LiteYouTubeElement };

export const getYouTubeVideoId = (url: string) => {
  const pattern =
    /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtu\.be\/|youtube\.com\/(?:shorts\/|v\/|embed\/|watch\?.*v=))([a-zA-Z0-9_-]{11})/;

  const match = url.match(pattern);
  return match ? match[1] : undefined;
};

export const YouTubePlayer = ({
  videoUrl = '',
  orientation = 'Landscape',
  landscapeThumbnailUrl,
  portraitThumbnailUrl,
  onStateChange,
}: YouTubePlayerProps) => {
  const videoId = getYouTubeVideoId(videoUrl);
  const liteYouTubeRef = useRef<LiteYouTubeElement | null>(null);
  const thumbnail = orientation === 'Portrait' ? portraitThumbnailUrl : landscapeThumbnailUrl;

  useEffect(() => {
    if (!videoId) return;
    const self = liteYouTubeRef.current;

    const handleOtherPlaying = (event: Event) => {
      const source = (event as CustomEvent<YouTubePlayingEventDetail>).detail?.source;
      if (!self || !source || source === self) return;
      if (!self.querySelector('iframe')) return;

      self
        .getYTPlayer?.()
        .then(player => player.pauseVideo())
        .catch(() => {});
    };

    const handleMessage = (event: MessageEvent) => {
      if (!YOUTUBE_ORIGINS.includes(event.origin)) return;

      const data = tryParseJson<YouTubeMessageEvent>(event.data);
      if (data?.event !== 'infoDelivery' && data?.event !== 'onStateChange') return;

      const playerState = data.info?.playerState;
      if (typeof playerState !== 'number') return;

      if (!self) return;
      const iframeWindow = self.querySelector('iframe')?.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow) return;

      if (data.event === 'onStateChange' && playerState === PlayerState.PLAYING) {
        window.dispatchEvent(
          new CustomEvent<YouTubePlayingEventDetail>(YOUTUBE_PLAYER_PLAYING_EVENT, {
            detail: { source: self },
          })
        );
      }
      onStateChange?.({
        state: playerState as PlayerState,
        currentTime: data.info?.currentTime,
        duration: data.info?.duration,
      });
    };

    window.addEventListener(YOUTUBE_PLAYER_PLAYING_EVENT, handleOtherPlaying);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener(YOUTUBE_PLAYER_PLAYING_EVENT, handleOtherPlaying);
      window.removeEventListener('message', handleMessage);
    };
  }, [onStateChange, videoId]);

  return (
    <div className="chromatic-ignore">
      <AspectRatio ratio={orientation === 'Portrait' ? ASPECT_RATIOS.portrait : ASPECT_RATIOS.landscape}>
        <lite-youtube
          ref={liteYouTubeRef}
          videoid={videoId || ''}
          class={styles.player}
          js-api={true}
          style={thumbnail ? { backgroundImage: `url("${thumbnail}")` } : undefined}
        />
      </AspectRatio>
    </div>
  );
};
